import React, { useState, useEffect, useContext } from "react";
import { CircularProgress } from "@mui/material";
import {
  cleanSessionCookie,
  SessionContext,
  setSessionCookie,
} from "../../helpers/session";
import { useNavigate } from "react-router-dom";
import { login } from "../../services/userService";
import useMobile from "../../hooks/useOrientation";
import darkLogo from "../../assets/dark-logo-transparent.png";
import { useDispatch } from "react-redux";
import { addUser } from "../../store/actions/userActions";
import { processErrorResponse } from "../../helpers/processErrorResponse";
import Bugsnag from "@bugsnag/browser";
import { ValidateEmail, ValidatePassword } from "../shared/regex";

const APP_VERSION = process.env.REACT_APP_VERSION;
const primary = "#00D2B7";

const LoginScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const isMobile = useMobile();
  const { setUser } = useContext(SessionContext);

  const [formData, setFormData] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState();

  useEffect(() => {
    cleanSessionCookie();
  }, []);

  const handleChangeForm = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmitLogin = () => {
    if (!ValidateEmail(formData.username)) {
      setMessage("Email no válido.");
      return;
    }
    if (!ValidatePassword(formData.password)) {
      setMessage("Contraseña no válida.");
      return;
    }
    setLoading(true);
    login(formData.username.toLocaleLowerCase(), formData.password)
      .then((res) => {
        setLoading(false);
        setUser(res);
        dispatch(addUser(res));
        setSessionCookie(res.token, res);
        Bugsnag.notify("Login.", function (event) {
          event.severity = "info";
          event.addMetadata("userData", {
            user: formData.username,
            isPasswordInsecure: res.phone === formData.password,
            version: APP_VERSION,
          });
        });
        navigate("/perfil", {
          state: { isPasswordInsecure: res.phone === formData.password },
        });
      })
      .catch((e) => {
        setLoading(false);
        setMessage(processErrorResponse(e)[1]);
      });
  };

  const handleKeypress = (e) => {
    if (e.keyCode === 13) handleSubmitLogin();
  };

  return (
    <div style={styles.page}>
      {/* Left panel — branding (desktop only) */}
      {!isMobile && (
        <div style={styles.leftPanel}>
          <img src={darkLogo} alt="College" style={styles.brandLogo} />
          <p style={styles.brandTagline}>El viaje de tus sueños</p>
        </div>
      )}

      {/* Right panel — form */}
      <div
        style={{
          ...styles.rightPanel,
          padding: isMobile ? "32px 24px" : "48px 56px",
        }}
      >
        {isMobile && (
          <img src={darkLogo} alt="College" style={styles.mobileLogo} />
        )}

        <h1 style={styles.title}>Bienvenido</h1>
        <p style={styles.subtitle}>Ingresá a tu cuenta para continuar</p>

        {/* Email */}
        <div style={styles.fieldGroup}>
          <label style={styles.label}>Email</label>
          <div style={styles.inputWrapper}>
            <svg
              style={styles.inputIcon}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
            >
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
            <input
              style={styles.input}
              type="email"
              placeholder="tu@email.com"
              maxLength={50}
              onChange={(e) => handleChangeForm("username", e.target.value)}
              onKeyDown={handleKeypress}
              autoComplete="email"
            />
          </div>
        </div>

        {/* Password */}
        <div style={styles.fieldGroup}>
          <label style={styles.label}>Contraseña</label>
          <div style={styles.inputWrapper}>
            <svg
              style={styles.inputIcon}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <input
              style={styles.input}
              type={passwordVisible ? "text" : "password"}
              placeholder="••••••••"
              maxLength={50}
              onChange={(e) => handleChangeForm("password", e.target.value)}
              onKeyDown={handleKeypress}
              autoComplete="current-password"
            />
            <button
              style={styles.eyeBtn}
              onClick={() => setPasswordVisible(!passwordVisible)}
              tabIndex={-1}
              type="button"
            >
              {passwordVisible ? (
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Error */}
        {message && <div style={styles.errorBanner}>{message}</div>}

        {/* Login button */}
        <button
          style={{ ...styles.loginBtn, opacity: loading ? 0.7 : 1 }}
          onClick={handleSubmitLogin}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={20} style={{ color: "#fff" }} />
          ) : (
            "Ingresar"
          )}
        </button>

        {/* Divider */}
        <div style={styles.divider}>
          <div style={styles.dividerLine} />
          <span style={styles.dividerText}>o</span>
          <div style={styles.dividerLine} />
        </div>

        {/* Secondary actions */}
        <button
          style={styles.secondaryBtn}
          onClick={() => navigate("/registro")}
        >
          Registrarse
        </button>

        <button style={styles.linkBtn} onClick={() => navigate("/recuperar")}>
          Olvidé mi contraseña
        </button>
      </div>
    </div>
  );
};

export default LoginScreen;

// ---- Styles ----

const styles = {
  page: {
    display: "flex",
    height: "100vh",
    backgroundColor: "#1e1e1e",
    overflow: "hidden",
  },

  /* Left branding panel */
  leftPanel: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(145deg, #1a1a1a 0%, #2a2a2a 100%)",
    borderRight: "1px solid #333",
    gap: 20,
  },
  brandLogo: {
    width: 120,
  },
  brandTagline: {
    color: "#666",
    fontSize: 12,
    letterSpacing: 1,
  },

  /* Right form panel */
  rightPanel: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    maxWidth: 480,
    width: "100%",
    margin: "0 auto",
    overflowY: "auto",
  },
  mobileLogo: {
    width: 80,
    alignSelf: "center",
    marginBottom: 24,
  },
  title: {
    fontSize: 26,
    fontWeight: 700,
    color: "#fff",
    marginBottom: 6,
    fontFamily: "Gilroy-Bold",
  },
  subtitle: {
    fontSize: 12,
    color: "#888",
    marginBottom: 32,
  },

  /* Fields */
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    marginBottom: 16,
  },
  label: {
    fontSize: 12,
    color: "#aaa",
    fontWeight: 500,
  },
  inputWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  inputIcon: {
    position: "absolute",
    left: 14,
    width: 17,
    height: 17,
    color: "#555",
    pointerEvents: "none",
  },
  input: {
    width: "100%",
    padding: "12px 44px",
    background: "#2a2a2a",
    border: "1.5px solid #3a3a3a",
    borderRadius: 10,
    color: "#fff",
    fontSize: 16,
    outline: "none",
    fontFamily: "Gilroy",
    transition: "border-color 0.15s",
  },
  eyeBtn: {
    position: "absolute",
    right: 12,
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#666",
    display: "flex",
    alignItems: "center",
    padding: 4,
  },

  /* Error */
  errorBanner: {
    background: "#3a1a1a",
    border: "1px solid #7a2a2a",
    borderRadius: 8,
    padding: "10px 14px",
    color: "#f08080",
    fontSize: 12,
    marginBottom: 12,
  },

  /* Buttons */
  loginBtn: {
    width: "100%",
    padding: "13px",
    background: primary,
    color: "#fff",
    border: "none",
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "Gilroy-Bold",
    marginTop: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    transition: "opacity 0.15s",
  },
  divider: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    margin: "24px 0",
  },
  dividerLine: {
    flex: 1,
    height: 1,
    background: "#333",
  },
  dividerText: {
    color: "#555",
    fontSize: 12,
  },
  secondaryBtn: {
    width: "100%",
    padding: "12px",
    background: "transparent",
    border: "1.5px solid #3a3a3a",
    borderRadius: 10,
    color: "#ccc",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "Gilroy",
    marginBottom: 12,
    transition: "border-color 0.15s",
  },
  linkBtn: {
    background: "none",
    border: "none",
    color: "#666",
    fontSize: 12,
    cursor: "pointer",
    textDecoration: "underline",
    padding: "4px 0",
    fontFamily: "Gilroy",
    alignSelf: "center",
    width: "100%",
    textAlign: "center",
  },
};
